import React, { useRef } from 'react';
import { RefreshCw, Search, Filter, Cable, X, ChevronsDownUp, PanelLeftClose } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useShortcutLabel } from '../../keybindings/hooks/useShortcutLabel';

interface ExplorerToolbarProps {
  title?: string;
  onRefresh?: () => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  onAdd?: () => void;
  onFilter?: () => void;
  onCollapseAll?: () => void;
  onCollapseSidebar?: () => void;
  renderCustomActions?: () => React.ReactNode;
}

/** Tactile press feedback shared by the toolbar's buttons (scale on press). */
const pressable = 'active:scale-[0.96] transition-[color,box-shadow,scale]';

export function ExplorerToolbar({
  title,
  onRefresh,
  searchTerm,
  onSearchChange,
  onAdd,
  onFilter,
  onCollapseAll,
  onCollapseSidebar,
  renderCustomActions,
}: ExplorerToolbarProps) {
  const toggleSidebarShortcutLabel = useShortcutLabel('global.toggleSidebar');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchClear = () => {
    onSearchChange?.('');
    searchInputRef.current?.focus();
  };

  return (
    <div className="flex flex-col border-b border-border bg-muted/40 select-none">
      <div className="flex items-center justify-between px-2 py-1">
        <div className="flex items-center space-x-1">
          {onAdd && (
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 px-2 text-xs ${pressable}`}
              title="Open connections"
              onClick={onAdd}
              data-testid="explorer-connect-button"
            >
              <Cable className="h-3.5 w-3.5 mr-1" />
              Connect
            </Button>
          )}
          {title && <div className="text-sm font-medium">{title}</div>}
        </div>

        <div className="flex items-center space-x-1">
          {onCollapseAll && (
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 ${pressable}`}
              title="Collapse all"
              onClick={onCollapseAll}
              data-testid="explorer-collapse-all-button"
            >
              <ChevronsDownUp className="h-4 w-4" />
            </Button>
          )}

          {onRefresh && (
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 ${pressable}`}
              title="Refresh selected node"
              onClick={onRefresh}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}

          {onFilter && (
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 ${pressable}`}
              title="Filter"
              onClick={onFilter}
            >
              <Filter className="h-4 w-4" />
            </Button>
          )}

          {renderCustomActions && renderCustomActions()}

          {onCollapseSidebar && (
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 ${pressable}`}
              title={`Collapse sidebar${toggleSidebarShortcutLabel ? ` (${toggleSidebarShortcutLabel})` : ''}`}
              onClick={onCollapseSidebar}
              data-testid="explorer-collapse-sidebar-button"
            >
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {onSearchChange && (
        <div className="px-2 pb-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search objects..."
              value={searchTerm ?? ''}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-7 pl-7 pr-7 text-xs"
              data-testid="explorer-search-input"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="icon"
                className={`absolute right-0 top-0 h-7 w-7 ${pressable}`}
                onClick={handleSearchClear}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
